/**
 * PLATFORM-1 · Phase 7 — Ops Hub · AI Decisions Queue
 *
 * Operator surface at /operations-hub/ai-decisions. Shows AI-Ops policy
 * output for the active hotel: pending / acknowledged / executed / dismissed
 * decisions with inline actions. "Evaluate now" force-runs pattern detection
 * + policy pass (idempotent by 60-min cooldown).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Radar, Check, X, Zap, RefreshCw, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useHotelContext } from "@/contexts/HotelContext";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "pending",      label: "Pending",      testid: "ai-decisions-tab-pending" },
  { key: "acknowledged", label: "Acknowledged", testid: "ai-decisions-tab-ack" },
  { key: "executed",     label: "Executed",     testid: "ai-decisions-tab-exec" },
  { key: "dismissed",    label: "Dismissed",    testid: "ai-decisions-tab-dismissed" },
];

function sevChipCls(sev) {
  switch (sev) {
    case "critical": return "bg-rose-100 text-rose-800 border-rose-200";
    case "warning":  return "bg-amber-100 text-amber-800 border-amber-200";
    default:         return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AIDecisions() {
  const { ctx } = useHotelContext() || {};
  const hotelId = ctx?.hotel_id;
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/ai-ops/decisions", {
        params: { hotel_id: hotelId, status, limit: 100 },
      });
      setItems(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load decisions");
    } finally { setLoading(false); }
  }, [hotelId, status]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const evaluate = useCallback(async () => {
    if (!hotelId) return;
    setEvaluating(true);
    try {
      const { data } = await api.post("/ai-ops/decisions/evaluate", {
        hotel_id: hotelId, window_hours: 24,
      });
      const n = data?.decisions_inserted ?? 0;
      if (n > 0) toast.success(`${n} new decision${n === 1 ? "" : "s"} added to the queue`);
      else       toast.info(`No new decisions — ${data?.patterns_evaluated ?? 0} pattern(s) checked (all within cooldown or non-actionable).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluation failed");
    } finally { setEvaluating(false); }
  }, [hotelId, load]);

  const act = useCallback(async (id, action) => {
    try {
      await api.post(`/ai-ops/decisions/${id}/${action}`);
      toast.success(`Decision ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action} decision`);
    }
  }, [load]);

  const counts = useMemo(() => ({ [status]: items.length }), [status, items.length]);

  if (!hotelId) {
    return <div className="text-sm text-slate-500 p-6" data-testid="ai-decisions-no-hotel">Select a hotel to view AI decisions.</div>;
  }

  return (
    <div className="space-y-5" data-testid="ai-decisions-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-hocp-gold flex items-center justify-center shrink-0">
            <Radar className="w-5 h-5 text-hocp-eerie" strokeWidth={2}/>
          </div>
          <div>
            <h2 className="font-display text-[20px] font-bold text-hocp-navy leading-tight">AI Decisions</h2>
            <p className="text-[12.5px] text-slate-500 mt-1">
              Descriptive AI-Ops output. Human-in-the-loop — nothing executes until you act.
            </p>
          </div>
        </div>
        <Button
          data-testid="ai-decisions-evaluate"
          onClick={evaluate} disabled={evaluating}
          className="bg-hocp-mint hover:bg-hocp-mintStrong text-hocp-deepTeal font-semibold"
        >
          <Zap className={`w-4 h-4 mr-2 ${evaluating ? "animate-pulse" : ""}`}/>
          {evaluating ? "Evaluating…" : "Evaluate now"}
        </Button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={t.testid}
            onClick={() => setStatus(t.key)}
            className={`px-4 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${
              status === t.key
                ? "border-hocp-navy text-hocp-navy"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}{status === t.key ? ` (${counts[t.key] ?? 0})` : ""}
          </button>
        ))}
        <button
          data-testid="ai-decisions-refresh"
          onClick={load}
          className="ml-auto text-[12.5px] text-slate-500 hover:text-slate-800 px-2 flex items-center gap-1"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/> Refresh
        </button>
      </div>

      {/* Errors */}
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-[12.5px] text-rose-800"
             data-testid="ai-decisions-error">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="ai-decisions-table">
        {items.length === 0 && !loading && (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="ai-decisions-empty">
            {status === "pending"
              ? "No pending decisions. The queue is calm."
              : `No ${status} decisions.`}
          </div>
        )}
        {items.map((d) => (
          <div
            key={d._id}
            data-testid={`ai-decision-${d._id}`}
            className="p-4 border-b border-slate-100 last:border-b-0 hover:bg-amber-50/30"
          >
            <div className="flex items-start gap-3">
              <Badge variant="outline" className={`shrink-0 ${sevChipCls(d.source_pattern?.severity)}`}>
                {d.source_pattern?.severity || "info"}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-hocp-navy leading-tight">
                  {d.summary}
                </div>
                <div className="mt-1 text-[11.5px] text-slate-500 flex flex-wrap items-center gap-2 font-mono">
                  <span>{d.code}</span>
                  <ChevronRight className="w-3 h-3 opacity-40"/>
                  <span>{d.source_pattern?.code || "—"}</span>
                  <span className="opacity-60">·</span>
                  <span>{fmtTime(d.created_at)}</span>
                </div>
                {d.payload?.cta?.route && (
                  <div className="mt-2">
                    <Badge variant="outline" className="text-[10.5px]">
                      Suggested next step: {d.payload.cta.label} ({d.payload.cta.route})
                    </Badge>
                  </div>
                )}
              </div>
              {status === "pending" && (
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline"
                          data-testid={`ai-decision-${d._id}-ack`}
                          onClick={() => act(d._id, "acknowledge")}>
                    <Check className="w-3.5 h-3.5 mr-1"/> Acknowledge
                  </Button>
                  <Button size="sm" variant="outline"
                          data-testid={`ai-decision-${d._id}-dismiss`}
                          onClick={() => act(d._id, "dismiss")}>
                    <X className="w-3.5 h-3.5 mr-1"/> Dismiss
                  </Button>
                </div>
              )}
              {status === "acknowledged" && (
                <div className="flex gap-2 shrink-0">
                  <Button size="sm"
                          data-testid={`ai-decision-${d._id}-execute`}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => act(d._id, "execute")}>
                    <Zap className="w-3.5 h-3.5 mr-1"/> Mark executed
                  </Button>
                  <Button size="sm" variant="outline"
                          data-testid={`ai-decision-${d._id}-dismiss2`}
                          onClick={() => act(d._id, "dismiss")}>
                    <X className="w-3.5 h-3.5 mr-1"/> Dismiss
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
